import { Component, OnDestroy, OnInit, Input, ViewChild } from '@angular/core';
import { UntypedFormControl, NgForm, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { LogCategory, LoggerService, LogType, LogPage } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { PeopleService, PeopleSearchCriteria } from '../../services/people.service';
import { FacultyService } from '../../services/faculty.service';
import { UtilityService } from '../../core/services/utility.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GroupEmailComponent } from './group/group-email.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { TagManagerService } from '../../core/services/tag-manager.service';

@Component({
    selector: 'app-people',
    templateUrl: './people.component.html',
    styleUrls: ['./people.component.scss'],
    standalone: false
})
export class PeopleComponent implements OnInit, OnDestroy
{
    @Input() groupEmail: GroupEmailComponent;

    public mainTabToShow: string; // top level tabs
    public tabToShow: string; // students
    private subParams: Subscription;

    public searchCategory: string;
    public clearCategory: boolean;
    public isSearching: boolean;
    public isSearchRefined: boolean;
       
    public typeAhead$: Observable<any>;
    public legacy$: Observable<any>; 
    public legacyName$: Observable<string>;  
    public classmates$: Observable<any>;
    public classOf$: Observable<any>; 
    public instructors$: Observable<any>; 
    public sections$: Observable<any>;

    public facultyStudents$: Observable<any>;

    public searchTermControl: FormControl<string|null> = new FormControl<string|null>(null); 
    public selectedSectionControl: FormControl<number|null> = new FormControl<number|null>(null);

    public isAdvancedSearchRunning: boolean;
    public showFilter: boolean = false;

    //Search All People criteria    
    public searchType: string = "CurrentStudents";
    public formObject = {programs:''} // added this to make the programs drop-down list editable
    public program: string[] = [];
    public classOf: number = 0;
    public entryYear: string = "";
    public cohort: string = "";
    public legacy: string = "";
    public classification: string = "";    
    public exchange: number;
    public academicArea: string = "";
    public division: string = "";
    public searchTerm: string = "";
    public defaultProgram: string = "EMBA-E";
    //End of filters
    
    public advancedSearchDisplay: string;
    public advancedSearchError: string = "";
    public isFaculty: boolean = false;    

    public groups$: Observable<any>;
    public advancedSearch$: Observable<any>;

    public legacyCount: number = 0;
    public classmatesCount: number = 0; 
    public classOfCount: number = 0; 
    public instructorCount: number = 0;

    currentDate = this.util.getCurrentDate();

    @ViewChild('multiPrograms') multiPrograms;

    constructor(
        public auth: AuthenticationService,
        private modalService: NgbModal,
        public people: PeopleService,
        public faculty: FacultyService,
        private gtm: TagManagerService,        
        private logger: LoggerService,
        public profile: ProfileService,
        private route: ActivatedRoute,
        private util: UtilityService,       
    ) {

        //this.program = this.profile.programFriendly;     
        // This emits the name of the current user's legacy
        this.legacyName$ = profile.profile$
            .pipe(
                map(profile => util.getNestedValue(profile, "academic.legacy", '')),
            );

        // This emits any new search term, debounced to 1/2 second. For example, if the user types 's', 'm', 'i' and
        // then pauses for 1/2 second, then "smi" will be emitted. This starts with a blank search term. 
        const searchTerm$ = this.searchTermControl.valueChanges
            .pipe(
                startWith(''),
                debounceTime(300),
                distinctUntilChanged(),
                tap((term) => {
                    this.log('searchTerm$', `emitted ${term}`, LogCategory.SubscriptionData);
                    this.clearCategory = true; 
                }),
        );

        // This emits the search result for any emitted search term, starting with the IDs of all the people in the
        // current user's network. The result includes a status (status), a list of IDs (result), and a list of
        // type - ahead terms(terms) and matches that were found for those terms.
        this.searchCategory = '';
        const filterResult$ = combineLatest(people.network$, searchTerm$)
            .pipe(
            tap(() => { this.isSearching = true; } ) ,
                switchMap(([people, term]) => this.profile.searchProfiles$(term, this.searchCategory, people.map(a => a.PersonId), this.isSearchRefined)),
                tap((result) => {
                    this.log('filterResult$', `emitted`, LogCategory.SubscriptionData);
                    if (this.clearCategory) this.searchCategory = '';
                    this.isSearching = false;
                    this.isSearchRefined = false;
                }),
                shareReplay(1),
        );

        // This emits a list of type-ahead data from the most recent filter result, if available. This is an
        // associative array of profle keys > search terms > counts that match the most recent search term.
        // For example...
        //      'Interests' :
        //          'Blacksmithing' : 1
        //      'Names' :  
        //          'Smith' : 4 
        this.typeAhead$ = filterResult$
            .pipe(
                map((data) => { return data.terms; }),
        );

        // This emits a list of network people filtered by the filter result above, or a complete list if the
        // filter result status was "Not Attempted" (e.g. not enough characters typed).
        const filteredPeople$ = combineLatest(filterResult$, people.network$)
            .pipe(
                map(([filter, people]) => {
                    if (people)
                        people = this.setPhotoUrls(people);

                    if (filter.status === 'NotAttempted')
                        return people;
                    else
                        return people.filter(person => !!filter.result[person.PersonId])
                }),
                tap((result) => {
                    this.log('filteredPeople$', `emitted`, LogCategory.SubscriptionData);
                }),
                shareReplay(1),
            );

        // This emits the currently filtered and unflattened array of legacy people 
        this.legacy$ = filteredPeople$.pipe(
            map((data) => {
                const result = data.filter(a => a.IsLegacy);
                this.legacyCount = result.length;
                return result;
            }),
            tap(_ => this.log('legacy$', 'emitted', LogCategory.SubscriptionData)),
        );

        // This emits the currently filtered and unflattened array of classmates, filtered by the selected
        // section (or not filtered if "all sections" is selected)
        this.classmates$ = combineLatest(filteredPeople$, this.selectedSectionControl.valueChanges.pipe(startWith("")), this.route.queryParams)
            .pipe(
                map(([people, selected, query]) => {
                    // If there's a section already selected, we'll always show that one. If there's not one
                    // selected, then we'll show what's on the querystring. If that's not there, then show none
                    var selectedId: number;
                   
                    if ( (selected == null || selected == '') && query.sectionid != undefined)
                        selectedId = parseInt(query.sectionid);
                    else if ( (typeof selected === 'string') && (parseInt(selected) > 0) )
                        selectedId = parseInt(selected.toString());
                    else
                        selectedId = 0;

                    this.selectedSectionControl.setValue(selectedId, { emitEvent: false });

                    const result = (selectedId > 0)
                        ? people.filter(a => a.IsClassmate && a.InSections.includes(selectedId))
                        : people.filter(a => a.IsClassmate);

                    this.classmatesCount = result.length;
                    return result;
                }),
                tap(_ => this.log('classmates$', 'emitted', LogCategory.SubscriptionData)),
            );

        // This emits the currently filtered and unflattened array of "class of" students
        this.classOf$ = filteredPeople$.pipe(
            map((data) => {
                const result = data.filter(a => a.IsClassOf);
                this.classOfCount = result.length;
                return result;
            }),
            tap(_ => this.log('classOf$', 'emitted', LogCategory.SubscriptionData)),
        ); 

        // This emits the currently filtered and unflattened array of instructors
        this.instructors$ = filteredPeople$.pipe(
            map((data) => {
                const result = data.filter(a => a.IsInstructor);
                this.instructorCount = result.length;
                return result;
            }),
            tap(_ => this.log('instructors$', 'emitted', LogCategory.SubscriptionData)),
        );
                
        this.sections$ = profile.profile$
            .pipe(
                switchMap((profile) => {
                    return profile.employeeData.FacultyType != "Unknown" ? faculty.sections$ : people.sectionGroups$
                }),
        );

        //  Fetches the custom groups and attaches the photo URLs to the people
        this.groups$ = this.people.groups$.pipe(
            map((data) => {
                if (data)
                    data.forEach(g => g.Members = this.setPhotoUrls(g.Members));
                return data;
            })
        );

        //SET THE DEFAULT PROGRAM FOR THE PROGRAM PICKER
        if (this.profile.programFriendly)
            this.defaultProgram = this.profile.programFriendly;

        this.log("constructor", "", LogCategory.Constructor);
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Page, LogPage.People, category);
    }
   
    public ngOnInit() {

        this.log("ngOnInit", null, LogCategory.Init);        
        this.subParams = this.route.queryParams
            .subscribe(params => {
                this.tabToShow = params['tab'] || 'classmates';
                this.mainTabToShow = params['maintab'] || 'main-classmates';
            });
        if (this.auth.hasFeature("SearchFaculty"))
            this.mainTabToShow = 'mystudents';
        else if (!this.auth.hasFeature("SearchStudents"))
            this.mainTabToShow = 'searchallpeople';

    }

    public parseDate(date: string) {
        return Date.parse(date);
    }

    public ngOnDestroy() {
        this.log("ngOnDestroy", "Unsubscribing network$, search", LogCategory.Destroy);

        // We probably don't need to unsubscribe from this because it's probably finite, but it doesn't hurt. 
        if (!!this.subParams) {
            this.subParams.unsubscribe();
        }
    }

    public emailGroup(openedGroup): void {
        const modalRef = this.modalService.open(GroupEmailComponent);
        modalRef.componentInstance.description = openedGroup.Name;
        modalRef.componentInstance.mailtos = openedGroup.Members.map(a=>a.Email).join(";");
    }

    public updateSearch(term, category) {
        //ga - log search
        this.gtm.gaSearch('Classmates');
        this.searchCategory = category;
        this.isSearchRefined = true;
        this.searchTermControl.setValue(term);
    }

    public setProgram(items: any[]) {
        this.program = items;
    }

    public onAdvancedSearch() {
        this.advancedSearchError = '';
        var isValid = false;
        this.advancedSearch$ = null;      
        if (this.searchType == 'Faculty') {
            isValid = ((this.academicArea != null) && (this.academicArea != '')) || ((this.searchTerm != null) && (this.searchTerm.trim() != ''));
        }
        else if (this.searchType == 'Staff') {
            isValid = ((this.division != null) && (this.division != '')) || ((this.searchTerm != null) && (this.searchTerm.trim() != ''));
        }
        else {
            isValid =  (this.searchTerm != null) && (this.searchTerm.trim() != '') && (this.program.length > 0 );
        }
        // allow search if there is an academic area OR a serch term
        if (isValid) {
            //ga - log search
            this.gtm.gaSearch(`All People: ${this.searchType}`);
            this.isAdvancedSearchRunning = true;         
            if (this.searchTerm != null)
                this.searchTerm = this.searchTerm.trim();   
            
            var criteria = new PeopleSearchCriteria(this.searchTerm, this.searchType, this.program, this.classOf, this.cohort, this.legacy, this.entryYear, this.classification, this.exchange, this.academicArea, this.division);

            this.advancedSearch$ = this.people.advancedProfileSearch$(criteria).pipe(
                map((data) => {
                    data.forEach(group => {
                        let people = group.People;
                        people = this.setPhotoUrls(people);
                    })
                    return data;
                }),
                tap(() => { this.isAdvancedSearchRunning = false; }),
            );
        } else {
            this.advancedSearchError = "Type something in the box above";
            switch (this.searchType) {
                case "Faculty": {
                    this.advancedSearchError = this.advancedSearchError + " or choose an Academic Area to search";
                    break;
                }
                case "Staff": {
                    this.advancedSearchError = this.advancedSearchError + " or choose a Department to search";
                    break;
                }
                default: {
                    this.advancedSearchError = this.advancedSearchError + " and choose a Program to search";
                    break;
                }
            }          
        }        
    }

    private setPhotoUrls(people: any[]): any[] {
        people.forEach(p => {
            p.photoUrl = this.profile.getPhotoUrl(p.PersonId),
            p.failedPhotoUrl = 'assets/images/profile_well.png'
        })

        return people;
    }

    public resetSearchFields(f: NgForm, defaults ) {
        this.advancedSearch$ = null;
        f.resetForm({ searchType: 'CurrentStudents', Program: [defaults.program] });
        if (this.multiPrograms)
            this.multiPrograms.resetSelection();
    }

    public hasKeywords(lookup, program: string[]) {        
        return lookup.AppliesTo.some( l => program.includes(l));
        
    }
     
    public getTotalPeople(programs) {
        var count = 0;
        for (let i = 0; i < programs.length; i++) {
            count += programs[i].People.length;
        }
        return count;
    }
      
    
}
